import React, { createContext, FC, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {createESR, listNotifications, listSysNotifications, viewAllErrorTickets} from "../actions/form";
import {
	Backdrop,
	Badge,
	Box,
	Button,
	CircularProgress,
	Dialog,
	DialogContent,
	Divider,
	IconButton,
	ListItemIcon,
	ListItemText,
	Menu,
	MenuItem,
	MenuList,
	Typography,
} from "@mui/material";
import { Outlet, useLocation, useNavigate } from "react-router-dom";

import Add from "@mui/icons-material/Add";
import NotificationsOutlined from "@mui/icons-material/NotificationsOutlined";
import MessageOutlinedIcon from '@mui/icons-material/MessageOutlined';
import CardTravelOutlined from "@mui/icons-material/CardTravelOutlined";
import HelpOutlineOutlined from "@mui/icons-material/HelpOutlineOutlined";
import SettingsOutlined from "@mui/icons-material/SettingsOutlined";
import CorporateFareOutlined from "@mui/icons-material/CorporateFareOutlined";
import MoreVert from "@mui/icons-material/MoreVert";
import { ESRState } from "../types/data";
import { useCurrentUser } from "../hooks/useCurrentUser";
import { Drawer } from "./Drawer";
import { DashboardOutlined, ErrorOutline } from "@mui/icons-material";

import { logout } from "../auth";
import { UsersGroupSelection } from "./UsersGroupSelection";
import { displayOrg } from "./Login";
import { getMyOrganizations } from "../api/organizations";
import { isDashboardUser } from "@/api/dashboard";

export const SidebarContext = createContext<{
	sidebarOpen: boolean;
	setSidebarOpen: (open: boolean) => void;
}>({
	sidebarOpen: true,
	setSidebarOpen: () => {},
});

export const SidebarLayout: FC = () => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const dev_mode = process.env.DEV_MODE || "false";

	const [user] = useCurrentUser();
	let isUserAllowedToViewOrCreateMessages = true;
	let isUserAdmin = false;
	if(user?.groups){
		const groups = user?.groups ?? [];
		isUserAllowedToViewOrCreateMessages = !groups.includes("IDNR");
		if (groups.includes("Admin")) {
			isUserAllowedToViewOrCreateMessages = true;
			isUserAdmin = true;
		}
	}

	const error = useSelector((state: ESRState) => state.error);

	const anchorElRef = useRef<HTMLButtonElement | null>(null);
	const [userActionMenuOpen, setUserActionMenuOpen] = useState(false);
	const [sidebarOpen, setSidebarOpen] = useState(true);

	const loading = useSelector(
		(state: ESRState) =>
			!!state.loading["__NEW_ESR__"] || !!state.loading["__GLOBAL_LOADING__"],
	);

	const location = useLocation();
	const errorTickets = useSelector((state: ESRState) => state.error_tickets);
	const notifications = useSelector((state: ESRState) => state.notifications);
	const system_notifications = useSelector((state: ESRState) => state.system_notifications);

	useEffect(() => {
		if (user !== undefined && user !== null) {
			dispatch(listNotifications());
			dispatch(listSysNotifications());
			dispatch(viewAllErrorTickets());
		}
	}, [user]);

	// Organizations page is open to admins and to anyone who belongs to an organization
	const [hasOrganization, setHasOrganization] = useState(false);
	useEffect(() => {
		if (user !== undefined && user !== null && !user.admin) {
			getMyOrganizations()
				.then((orgs) => setHasOrganization(orgs.length > 0))
				.catch((e) => console.error(e));
		}
	}, [user?.email]);

	const createNewESR = () => {
		dispatch(createESR(user?.email ?? ""));
	};

	const [open, setOpen] = useState(false);
	useEffect(() => {
		if (user?.groups.length === 0) {
			setOpen(true);
		}
	}, [user]);

	const unreadMessageCount = useSelector(
			(state: ESRState) => state.unread_message_count,
		);

	return (
		<SidebarContext.Provider value={{ sidebarOpen, setSidebarOpen }}>
			{/* Force user to choose groups if they have none */}
			{user && (
				<>
					<Dialog onClose={() => setOpen(false)} open={open}>
						<DialogContent>
							<UsersGroupSelection
								email={user.email}
								displayOrg={displayOrg}
								closer={() => setOpen(false)}
							/>
						</DialogContent>
					</Dialog>
				</>
			)}
			<Drawer />
			{/* {isUserAllowedToViewOrCreateMessages && <FloatingMessage />} */}
			<Box
				sx={{
					height: "100vh",
					display: "flex",
					flexDirection: "row",
					alignItems: "stretch",
				}}
			>
				<Box
					className="no-print"
					sx={{
						flex: "none",
						background: `white`,
						boxShadow: `0px 0px 6px 2px rgba(0, 62, 98, 0.12)`,
						width: "300px",
						display: "flex",
						flexDirection: "column",
						"& .MuiMenuItem-root": {
							height: "48px",
						},
						zIndex: 1,
						transform: sidebarOpen ? "none" : "translate(-100%)",
						transition: "all 0.2s ease-in-out",
					}}
				>
					<Box
						sx={{
							display: "flex",
							flexDirection: "row",
							alignItems: "center",
							justifyContent: "center",
							py: "24px",
							boxSizing: "border-box",
							height: "80px",
						}}
					>
						<Box
							component="img"
							src="https://idot.illinois.gov/content/dam/soi/en/web/idot/images/idotlogo.png"
							alt="Illinois Department of Transportation"
							sx={{ width: "130px", height: "40px" }}
						/>
						<Box
							sx={{
								width: "1px",
								height: "15px",
								background: "black",
								mx: "4px",
							}}
						/>
						<Typography fontWeight={700} fontSize={14}>
							TREC
						</Typography>
					</Box>

					<Divider />

					<MenuList>
						<MenuItem
							sx={
								location.pathname === "/"
									? {
											background: "rgba(57, 141, 239, 0.08)",
											borderLeft: "2px solid #1176E8",
											".MuiTypography-root": {
												fontWeight: "bold",
											},
										}
									: {
											borderLeft: "2px solid transparent",
										}
							}
							onClick={() => navigate("/")}
						>
							<ListItemIcon>
								<Badge
									badgeContent={
										notifications
											.flatMap((group) => group.notifications)
											.filter((notif) => notif.status === 0 && !notif.invalid)
											.length
									}
									color="error"
								>
									<NotificationsOutlined />
								</Badge>
							</ListItemIcon>
							<ListItemText>Notifications</ListItemText>
						</MenuItem>
						{isUserAllowedToViewOrCreateMessages && (
							<MenuItem
								sx={
									location.pathname === "/"
										? {
												background: "rgba(57, 141, 239, 0.08)",
												borderLeft: "2px solid #1176E8",
												".MuiTypography-root": {
													fontWeight: "bold",
												},
											}
										: {
												borderLeft: "2px solid transparent",
											}
								}
								onClick={() => navigate("/messageslist")}
							>
								<ListItemIcon>
									<Badge badgeContent={unreadMessageCount} color="error">
										<MessageOutlinedIcon />
									</Badge>
								</ListItemIcon>
								<ListItemText>Messages</ListItemText>
							</MenuItem>
						)}
						{isUserAdmin && (
							<MenuItem
								sx={
									location.pathname === "/"
										? {
												background: "rgba(57, 141, 239, 0.08)",
												borderLeft: "2px solid #1176E8",
												".MuiTypography-root": {
													fontWeight: "bold",
												},
											}
										: {
												borderLeft: "2px solid transparent",
											}
								}
								onClick={() => navigate("/systemnotification")}
							>
								<ListItemIcon>
									<Badge
										badgeContent={
											Object.values(system_notifications)
												.flat()
												.filter((notif) => notif.status === 0).length
										}
										color="error"
									>
										<NotificationsOutlined />
									</Badge>
								</ListItemIcon>
								<ListItemText>System Notifications</ListItemText>
							</MenuItem>
						)}
						<MenuItem onClick={() => navigate("/errortickets")}>
							<ListItemIcon>
								<Badge badgeContent={errorTickets.length} color="error">
									<ErrorOutline />
								</Badge>
							</ListItemIcon>
							<ListItemText>Error Tickets</ListItemText>
						</MenuItem>
						<MenuItem
							sx={
								location.pathname === "/all"
									? {
											background: "rgba(57, 141, 239, 0.08)",
											borderLeft: "2px solid #1176E8",
											".MuiTypography-root": {
												fontWeight: "bold",
											},
										}
									: {
											borderLeft: "2px solid transparent",
										}
							}
							onClick={() => navigate("/all")}
						>
							<ListItemIcon sx={{ fontWeight: 600 }}>
								<CardTravelOutlined />
							</ListItemIcon>
							<ListItemText>All Projects</ListItemText>
						</MenuItem>
						{isDashboardUser(user?.groups, user?.admin) && (
							<MenuItem
								sx={
									location.pathname === "/dashboard"
										? {
												background: "rgba(57, 141, 239, 0.08)",
												borderLeft: "2px solid #1176E8",
												".MuiTypography-root": {
													fontWeight: "bold",
												},
											}
										: {
												borderLeft: "2px solid transparent",
											}
								}
								onClick={() => navigate("/dashboard")}
							>
								<ListItemIcon>
									<DashboardOutlined />
								</ListItemIcon>
								<ListItemText>Dashboard</ListItemText>
							</MenuItem>
						)}
						{/* {dev_mode && <MenuItem
							sx={
								location.pathname === "/test_notifications"
									? {
											background: "rgba(57, 141, 239, 0.08)",
											borderLeft: "2px solid #1176E8",
											".MuiTypography-root": {
												fontWeight: "bold",
											},
										}
									: {
											borderLeft: "2px solid transparent",
										}
							}
							onClick={() => navigate("/test_notifications")}
						>
							<ListItemIcon>
								<NotificationImportantIcon />
							</ListItemIcon>
							<ListItemText>Test Notifications</ListItemText>
						</MenuItem>}
						{dev_mode && <MenuItem
							sx={
								location.pathname === "/test"
									? {
											background: "rgba(57, 141, 239, 0.08)",
											borderLeft: "2px solid #1176E8",
											".MuiTypography-root": {
												fontWeight: "bold",
											},
										}
									: {
											borderLeft: "2px solid transparent",
										}
							}
							onClick={() => navigate("/test")}
						>
							<ListItemIcon sx={{ fontWeight: 600 }}>
								<BugReport />
							</ListItemIcon>
							<ListItemText>Test Projects</ListItemText>
						</MenuItem>} */}
						<Box sx={{ mx: "12px", my: "16px" }}>
							<Button
								variant="contained"
								color="primary"
								size="large"
								fullWidth
								onClick={createNewESR}
							>
								<Add sx={{ mr: "8px" }} />
								New ESR (Project)
							</Button>
						</Box>
					</MenuList>

					<Box sx={{ flex: 1 }} />

					<MenuList>
						{user !== undefined && user !== null && user.admin ? (
							<MenuItem onClick={() => navigate("/usersmanagement")}>
								<ListItemIcon>
									<SettingsOutlined />
								</ListItemIcon>
								<ListItemText>User Management</ListItemText>
							</MenuItem>
						) : null}
						{user !== undefined &&
						user !== null &&
						(user.admin || hasOrganization) ? (
							<MenuItem onClick={() => navigate("/organizations")}>
								<ListItemIcon>
									<CorporateFareOutlined />
								</ListItemIcon>
								<ListItemText>Organizations</ListItemText>
							</MenuItem>
						) : null}

						<MenuItem onClick={() => navigate("/settings")}>
							<ListItemIcon>
								<SettingsOutlined />
							</ListItemIcon>
							<ListItemText>Account Settings</ListItemText>
						</MenuItem>
						<MenuItem onClick={() => navigate("/help")}>
							<ListItemIcon>
								<HelpOutlineOutlined />
							</ListItemIcon>
							<ListItemText>Help</ListItemText>
						</MenuItem>
						<Box
							sx={{ display: "flex", flexDirection: "columne", p: "6px 16px" }}
						>
							{user ? (
								<Box
									sx={{
										height: "32px",
										width: "32px",
										mr: "12px",
										borderRadius: "16px",
										background: "#5A9F2F",
										lineHeight: "32px",
										textAlign: "center",
									}}
								>
									{user.first_name?.charAt(0).toUpperCase()}
									{user.last_name?.charAt(0).toUpperCase()}
								</Box>
							) : (
								<></>
							)}
							{user ? (
								<>
									<ListItemText>
										{user.first_name} {user.last_name}
									</ListItemText>
								</>
							) : (
								<>
									<ListItemText>User</ListItemText>
								</>
							)}

							<IconButton
								aria-label="open user action menu"
								ref={anchorElRef}
								onClick={() => setUserActionMenuOpen(true)}
							>
								<MoreVert />
							</IconButton>
							<Menu
								anchorEl={anchorElRef.current}
								open={userActionMenuOpen}
								onClose={() => setUserActionMenuOpen(false)}
							>
								<MenuItem onClick={logout}>Logout</MenuItem>
							</Menu>
						</Box>
					</MenuList>
				</Box>
				<Box
					sx={{
						flex: "auto",
						overflowX: "hidden",
						overflowY: "scroll",
						position: "relative",
					}}
				>
					<Outlet />
				</Box>
			</Box>
			<Backdrop
				open={loading}
				sx={{ zIndex: (theme) => theme.zIndex.modal + 1 }}
			>
				<CircularProgress color="primary" />
			</Backdrop>
		</SidebarContext.Provider>
	);
};